import type { FollowUp, FollowUpType } from '../types'
import { supabase } from './supabaseClient'

type DbFollowUp = {
  id: string
  user_id: string
  application_id: string
  due_at: string
  title: string
  type: string
  note: string | null
  completed: boolean
  created_at: string
  updated_at: string
  applications?: {
    company: string
    position_title: string | null
  } | null
}

const toFollowUp = (row: DbFollowUp): FollowUp => ({
  id: row.id,
  userId: row.user_id,
  applicationId: row.application_id,
  dueAt: row.due_at,
  title: row.title,
  type: row.type as FollowUpType,
  note: row.note,
  completed: row.completed,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  application: row.applications
    ? {
        company: row.applications.company,
        positionTitle: row.applications.position_title,
      }
    : undefined,
})

export async function fetchFollowUps(userId: string): Promise<FollowUp[]> {
  const { data, error } = await supabase
    .from('follow_ups')
    .select(`
      *,
      applications (
        company,
        position_title
      )
    `)
    .eq('user_id', userId)
    .eq('completed', false)
    .order('due_at', { ascending: true })

  if (error) {
    console.error('Failed to fetch follow-ups:', error)
    throw error
  }

  return (data ?? []).map(toFollowUp)
}

export async function createFollowUp(
  userId: string,
  applicationId: string,
  data: {
    dueAt: string
    title: string
    type: FollowUpType
    note?: string
  }
): Promise<FollowUp> {
  const { data: row, error } = await supabase
    .from('follow_ups')
    .insert({
      user_id: userId,
      application_id: applicationId,
      due_at: data.dueAt,
      title: data.title,
      type: data.type,
      note: data.note ?? null,
    })
    .select(`
      *,
      applications (
        company,
        position_title
      )
    `)
    .single()

  if (error) {
    console.error('Failed to create follow-up:', error)
    throw error
  }

  return toFollowUp(row)
}

export async function updateFollowUp(
  id: string,
  data: Partial<{
    dueAt: string
    title: string
    type: FollowUpType
    note: string | null
    completed: boolean
  }>
): Promise<FollowUp> {
  const updateData: Record<string, unknown> = {}
  if (data.dueAt !== undefined) updateData.due_at = data.dueAt
  if (data.title !== undefined) updateData.title = data.title
  if (data.type !== undefined) updateData.type = data.type
  if (data.note !== undefined) updateData.note = data.note
  if (data.completed !== undefined) updateData.completed = data.completed

  const { data: row, error } = await supabase
    .from('follow_ups')
    .update(updateData)
    .eq('id', id)
    .select(`
      *,
      applications (
        company,
        position_title
      )
    `)
    .single()

  if (error) {
    console.error('Failed to update follow-up:', error)
    throw error
  }

  return toFollowUp(row)
}

export async function deleteFollowUp(id: string): Promise<void> {
  const { error } = await supabase.from('follow_ups').delete().eq('id', id)

  if (error) {
    console.error('Failed to delete follow-up:', error)
    throw error
  }
}

export async function markFollowUpComplete(id: string): Promise<FollowUp> {
  return updateFollowUp(id, { completed: true })
}
